/* eslint-disable max-classes-per-file */

export { EmailValidator } from 'commons-validator-js'

import { US_STATE_ABBREVIATIONS } from 'embed/constants'

import { LoanPurposeOptions } from '@evenfinancial/even-ts-static'

export interface BooleanValidator {
  isValid(bool: boolean, data: ValidationData): boolean
}

export interface NumberValidator {
  isValid(int: number, data: ValidationData): boolean
}

export interface StringValidator {
  isValid(str: string, data: ValidationData): boolean
}

export interface Validator {
  isValid(str: unknown, data?: ValidationData): boolean
}

// Supplemental data used during validation
export interface ValidationData {
  LoanPurpose?: LoanPurposeOptions
}

const currencyRegex = /^\$?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{2})?$/

const parseMonthAndYearOnlyDate = (date: string, delim = '/') => {
  if (date) {
    const parts = date.split(delim)
    const month = parseInt(parts[0], 10)
    const year = parseInt(parts[1], 10)
    const jsDate = new Date(year, month - 1, 1)
    return year === jsDate.getFullYear() && month === jsDate.getMonth() + 1 ? jsDate : null
  } else {
    return null
  }
}

const parseUSDate = (date: string, delim = '/') => {
  if (date) {
    const parts = date.split(delim)
    const month = parseInt(parts[0], 10)
    const dayOfMonth = parseInt(parts[1], 10)
    const year = parseInt(parts[2], 10)
    const jsDate = new Date(year, month - 1, dayOfMonth)
    return year === jsDate.getFullYear() && month === jsDate.getMonth() + 1 && dayOfMonth === jsDate.getDate()
      ? jsDate
      : null
  } else {
    return null
  }
}

export const DOBErrorMessage = () => {
  const oneHundredTwentyYearsAgo = yearsAgoToday(120).getFullYear()
  return `Please enter a birth year after ${oneHundredTwentyYearsAgo}`
}

const yearsAgoToday = (yearsAgo: number) => {
  const today = new Date()
  return new Date(today.getFullYear() - yearsAgo, today.getMonth(), today.getDate())
}

const ageIsValidAndOverEighteen = (date: Date) => yearsAgoToday(18).getTime() - date.getTime() >= 0

const ageIsValidAndYoungerThan120 = (date: Date) => yearsAgoToday(120).getTime() - date.getTime() <= 0
export class CurrencyValidator implements StringValidator {
  public isValid = (str) => currencyRegex.test(str)
}

export class DateMonthAndYearOnlyValidator implements StringValidator {
  public isValid = (str) => parseMonthAndYearOnlyDate(str, '/') !== null
}

export class DateValidator implements StringValidator {
  public isValid = (str) => parseUSDate(str, '/') !== null
}

export class DOBValidatorMinAge implements StringValidator {
  public isValid = (str) => {
    const date = parseUSDate(str, '/')
    return date !== null && ageIsValidAndOverEighteen(date)
  }
}

export class DOBValidatorAgeCap implements StringValidator {
  public isValid = (str) => {
    const date = parseUSDate(str, '/')
    return date !== null && ageIsValidAndYoungerThan120(date)
  }
}
export class IncomeValidator implements NumberValidator {
  public isValid = (int) => {
    const regex = /^[1-9]\d*$/
    return regex.test(int.toString())
  }
}

export class PasswordValidator implements StringValidator {
  public isValid = (str) => /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,64}$/.test(str)
}

export class WeightValidator implements StringValidator {
  public isValid = (str) => {
    const int = parseInt(str.replace(/\D/, ''), 10)
    return int >= 40 && int <= 999
  }
}

export class LoanAmountValidator implements Validator {
  public isValid = (val: string | number = '', data) =>
    typeof val === 'string' ? this.isStringValid(val, data) : this.isNumberValid(val, data)

  private readonly isStringValid = (str = '', data) => {
    const maxLoanAmount =
      data.LoanPurpose === LoanPurposeOptions.STUDENT_REFINANCE
        ? 500000
        : data.LoanPurpose === LoanPurposeOptions.AUTO_REFINANCE
        ? 250000
        : 100000
    const int = parseFloat(str.replace(/[^0-9.]/g, ''))
    return currencyRegex.test(str) && int <= maxLoanAmount && int > 0
  }

  private readonly isNumberValid = (int, data) => {
    const regexPattern =
      data.LoanPurpose === LoanPurposeOptions.STUDENT_REFINANCE
        ? '500000'
        : data.LoanPurpose === LoanPurposeOptions.AUTO_REFINANCE
        ? '250000'
        : '100000'
    const regex = new RegExp(`^[1-9]\\d{0,${regexPattern.length - 2}}$|^${regexPattern}$`)
    return regex.test(int.toString())
  }
}

export class VehicleIdNumberValidator implements StringValidator {
  public isValid = (input = '') => input.length === 17
}

export class BankRoutingNumberValidator implements StringValidator {
  public isValid = (str = '') => /^(\d{9})$/.test(str)
}

export class PhoneValidator implements StringValidator {
  public isValid = (str = '') => {
    // This is LeapLife's phone number validation. We're matching it here because the life insurance service makes
    // requests to LeapLife
    const regex = /^1?([2-9]..)([2-9]..)(\d{4})$/
    return regex.test(str.replace(/[\D]/g, ''))
  }
}

export class SocialSecurityValidator implements NumberValidator {
  public isValid = (str) => {
    const regex =
      /^(?!\b(\d)\1+-(\d)\1+-(\d)\1+\b)(?!123-45-6789|219-09-9999|078-05-1120)(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0{4})\d{4}$/
    return regex.test(str)
  }
}

export class TrueValidator implements BooleanValidator {
  public isValid = (bool) => bool === true
}

export class UsStateAbbreviationValidator implements StringValidator {
  public isValid = (str) => str !== undefined && US_STATE_ABBREVIATIONS.indexOf(str.toUpperCase()) > -1
}

export class ZipcodeValidator implements StringValidator {
  public isValid = (str) => {
    const regex = /^\d{5}([-\s]\d{4})?$/
    return regex.test(str)
  }
}
